import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import NotificationsButtonLayout from './NotificationsButtonLayout';
import { NotificationItemProps } from 'infrastructure/notification/types';
import { fetchNotificationData } from 'infrastructure/notification/redux/thunks';
import { useAppDispatch } from 'app/store/hooks';
import { RootState } from 'app/store/rootReducer';
import { useShortPolling } from 'framework/utils';

const TEN_MINUTES = 600000;

type NotificationsButtonProps = {
  items: NotificationItemProps[];
};

const NotificationsButton: React.FC<NotificationsButtonProps> = ({ items }) => {
  const dispatch = useAppDispatch();
  const responses = useSelector((state: RootState) => state.notifications);
  const hasAutoUpdatingItems = items.some((item: NotificationItemProps) => item.autoUpdate && item.loadData);

  useEffect(() => {
    dispatch(fetchNotificationData(items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAutoUpdatingItems = useCallback(() => {
    dispatch(fetchNotificationData(items, (item: NotificationItemProps) => item.autoUpdate === true));
  }, [dispatch, items]);

  useShortPolling({
    callback: fetchAutoUpdatingItems,
    enabled: hasAutoUpdatingItems,
    executeImmediately: false,
    interval: TEN_MINUTES,
  });

  const isItemVisible = (item: NotificationItemProps) => {
    const response = responses[item.type];
    if (typeof item.isVisible === 'function') {
      return item.isVisible(response);
    }
    return item.isVisible !== false;
  };

  const getItemCount = (item: NotificationItemProps) => {
    const count = typeof item.getCount === 'function' ? item.getCount(responses[item.type]) : 0;
    return Number.isFinite(count) ? Math.max(0, count) : 0;
  };

  const visibleItems = items.filter(item => isItemVisible(item) && getItemCount(item) > 0);
  const totalCount = visibleItems.reduce((sum, item) => sum + getItemCount(item), 0);

  return <NotificationsButtonLayout visibleItems={visibleItems} responses={responses} totalCount={totalCount} />;
};

export default NotificationsButton;
